<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";

/**
 * Segmented PIN entry for field login.
 *
 * The PIN is dictated over radio/phone in groups ("two-six-one, oh-seven-oh,
 * four-six-two"), so the cells are grouped the same way. The backend stores the
 * digest of the grouped `NNN-NNN-NNN` string, but a numeric soft keyboard has no
 * hyphen key -- this component owns the digits and lets the caller format them,
 * so the responder only ever types what they hear.
 *
 * `groups` is a prop because the PIN length/entropy is still open (HPO_REDESIGN
 * section 7); changing the format must not mean rewriting the input.
 */
const props = defineProps({
  modelValue: {type: String, default: ''},
  groups: {type: Array, default: () => [3, 3, 3]},
  disabled: {type: Boolean, default: false},
  invalid: {type: Boolean, default: false},
  autofocus: {type: Boolean, default: true},
});

const emit = defineEmits(['update:modelValue', 'complete']);

const size = computed(() => props.groups.reduce((total, n) => total + n, 0));

// Flat index of the first cell of each group, so the nested v-for can address
// the flat `digits` / `inputs` arrays.
const offsets = computed(() => {
  const result = [];
  let running = 0;
  for (const count of props.groups) {
    result.push(running);
    running += count;
  }
  return result;
});

const digits = ref(Array(size.value).fill(''));
const inputs = ref([]);

function setInputRef(el, index) {
  inputs.value[index] = el || null;
}

function focusCell(index) {
  const target = inputs.value[Math.max(0, Math.min(index, size.value - 1))];
  if (target) {
    target.focus();
    target.select();
  }
}

function currentValue() {
  return digits.value.join('');
}

function publish() {
  const value = currentValue();
  emit('update:modelValue', value);
  if (value.length === size.value) {
    emit('complete', value);
  }
}

/**
 * Write `text` into the cells starting at `start`, one digit per cell, and
 * return the index just past the last cell written. Non-digits are dropped, so
 * a pasted `261-070-462` and a typed `261070462` land identically.
 */
function distribute(text, start) {
  const incoming = (text || '').replace(/\D/g, '');
  let cursor = start;
  for (const char of incoming) {
    if (cursor >= size.value) break;
    digits.value[cursor] = char;
    cursor += 1;
  }
  return cursor;
}

function onInput(index, event) {
  // A soft keyboard, an autofill of the whole code, or fast typing can all
  // deliver more than one character to a single cell -- spread them forward
  // rather than truncating to the first.
  const raw = event.target.value;
  const cleaned = (raw || '').replace(/\D/g, '');

  if (!cleaned) {
    digits.value[index] = '';
    event.target.value = '';
    publish();
    return;
  }

  const next = distribute(cleaned, index);
  event.target.value = digits.value[index];
  publish();
  nextTick(() => focusCell(next));
}

function onKeydown(index, event) {
  switch (event.key) {
    case 'Backspace':
      // Deleting an empty cell steps back and clears the previous one, so
      // holding backspace walks the whole PIN out.
      if (!digits.value[index] && index > 0) {
        event.preventDefault();
        digits.value[index - 1] = '';
        publish();
        focusCell(index - 1);
      }
      break;
    case 'Delete':
      event.preventDefault();
      digits.value[index] = '';
      publish();
      break;
    case 'ArrowLeft':
      event.preventDefault();
      focusCell(index - 1);
      break;
    case 'ArrowRight':
      event.preventDefault();
      focusCell(index + 1);
      break;
    default:
      break;
  }
}

function onPaste(index, event) {
  event.preventDefault();
  const text = (event.clipboardData || window.clipboardData)?.getData('text') ?? '';
  const next = distribute(text, index);
  publish();
  nextTick(() => focusCell(next));
}

function clear() {
  digits.value = Array(size.value).fill('');
  emit('update:modelValue', '');
  nextTick(() => focusCell(0));
}

// Let the parent reset the field after a rejected PIN.
defineExpose({clear, focus: () => focusCell(0)});

watch(() => props.modelValue, (value) => {
  if (value === currentValue()) return;
  digits.value = Array(size.value).fill('');
  distribute(value, 0);
});

onMounted(() => {
  if (props.autofocus) focusCell(0);
});
</script>

<template>
  <div class="pin-code"
       :class="{'pin-code--invalid': invalid, 'pin-code--disabled': disabled}"
       role="group"
       :aria-label="pgettext('label', 'PIN')">
    <template v-for="(count, group) in groups" :key="`group-${group}`">
      <span v-if="group > 0" class="pin-code__separator" aria-hidden="true">–</span>

      <div class="pin-code__group">
        <input v-for="position in count"
               :key="`cell-${offsets[group] + position}`"
               :ref="el => setInputRef(el, offsets[group] + position - 1)"
               class="pin-code__cell"
               :class="{'pin-code__cell--filled': !!digits[offsets[group] + position - 1]}"
               :value="digits[offsets[group] + position - 1]"
               :disabled="disabled"
               :autocomplete="group === 0 && position === 1 ? 'one-time-code' : 'off'"
               :aria-label="interpolate(
                   pgettext('label', 'Digit %(position)s of %(total)s'),
                   {position: offsets[group] + position, total: size},
                   true)"
               type="text"
               inputmode="numeric"
               pattern="[0-9]*"
               maxlength="1"
               autocorrect="off"
               spellcheck="false"
               @input="onInput(offsets[group] + position - 1, $event)"
               @keydown="onKeydown(offsets[group] + position - 1, $event)"
               @paste="onPaste(offsets[group] + position - 1, $event)"
               @focus="$event.target.select()"/>
      </div>
    </template>
  </div>
</template>
